import * as React from 'react';
import { useEffect, useCallback } from 'react';
import { Switch, Route } from 'react-router-dom';
import { RouteTemplates } from '../../../navigation/routes';
import DevPlanItem from '../DevPlanItem';
import DevPlanView from '../DevPlanView';
import { Dispatch } from 'redux';
import { getDevPlanItemsAction } from '../api/getDevPlanItemsAction';
import { getDevPlanActionsAction } from '../devPlanAction/api/getDevPlanActionsAction';
import { getDevPlanCollaboratorsAction } from './api/getDevPlanCollaboratorsAction';
import { getDevItemCommentsAction } from './api/getDevItemCommentsAction';
import { useDispatch } from 'react-redux';
import { useMount } from '../../../core/hooks/useMount';
import { setPortalTypeAction } from '../../../core/redux/setPortalTypeAction';
import { PortalType } from '../../../navigation/portalType';
import { loadProjectSummaryAction } from '../../loadProjectSummaryAction';
import { useCollaborationProjIds } from './hooks';
import { PAGE_BG, PAGE_HEIGHT } from '../../../layout/pageStyle';
import { FLEX_COLUMN, FLEX_CENTERED } from '../../../layout/flexBox';
import { Spinner } from '../../../loading/Spinner';
import { COLORS } from '../../../globalStyle/colors';
import ErrorMsg from '../../../error/ErrorMsg';
import { Str } from '../../../localization/translationKeys';
import { css } from '@emotion/core';
import { getDevPlanStatusesAction } from '../devPlanAction/api/getDevPlanStatusesAction';
import CustomerService from '../../../customerService/CustomerService';
import { useAnyLoading, useAnyError } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';
import { getDevPlanFieldsAndAnswersAction } from '../devPlanAction/api/getDevPlanFieldsAndAnswersAction';
import { useAppState } from '../../../core/redux/hooks';


const styles = {
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


const API_ACTIONS = [
  ActionType.LoadProjectSummaryRequest,
  ActionType.GetDevPlanItemsRequest,
  ActionType.GetDevPlanActionsRequest,
  ActionType.GetDevPlanFieldsAndAnswersRequest,
  ActionType.GetDevPlanCollaboratorsRequest,
  ActionType.GetDevItemCommentsRequest,
  ActionType.GetStatusKeysRequest,
];


const loadProjectInfo = (dispatch: Dispatch) => (
  projectId: number,
  participantId: number,
) => {
  dispatch(loadProjectSummaryAction(projectId, participantId));
};

const loadPlanInfo = (dispatch: Dispatch) => (
  devPlanProjectId: number,
  participantId: number,
) => {
  dispatch(getDevPlanItemsAction(devPlanProjectId, participantId));
  dispatch(getDevPlanActionsAction(devPlanProjectId, participantId));
  dispatch(getDevPlanFieldsAndAnswersAction(devPlanProjectId, participantId));
  dispatch(getDevPlanStatusesAction());
  dispatch(getDevPlanCollaboratorsAction(devPlanProjectId, participantId));
  dispatch(getDevItemCommentsAction(devPlanProjectId, participantId));
};

/**
 * CollaboratorDevPlanRouter: a component that renders the dev plan views available to collaborators
 */
const CollaboratorDevPlanRouter: React.FC = () => {
  const dispatch = useDispatch();

  const { projId, participantId } = useCollaborationProjIds();

  const loadProject = useCallback(
    () => { loadProjectInfo(dispatch)(projId, participantId); },
    [dispatch, participantId, projId],
  );  

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projId);

  useMount(() => { dispatch(setPortalTypeAction(PortalType.COLLABORATOR)); });

  const loadData = useCallback(
    () => { loadPlanInfo(dispatch)(devPlanProjectId, participantId); },
    [dispatch, devPlanProjectId, participantId],
  );

  useEffect(
    () => {
      if (projId > 0 && participantId > 0 && devPlanProjectId > 0) {
        loadProject();
        loadData();
      }
    },
    [dispatch, loadProject, loadData, participantId, projId, devPlanProjectId],
  );

  const isLoading = useAnyLoading(API_ACTIONS);
  const hasError  = useAnyError(API_ACTIONS);

  if (isLoading || hasError) {
    return (
      <div css={[PAGE_BG, PAGE_HEIGHT, FLEX_COLUMN, FLEX_CENTERED]}>
        {
          isLoading
            ? <Spinner fill={COLORS.GREEN} css={styles.spinner} />
            : <ErrorMsg error={Str.LoadFailed} showRetry={true} onRetry={loadData} />
        }
      </div>
    );
  }

  return (
    <Switch>
      <Route path={RouteTemplates.CollaboratorCustomerService} component={CustomerService} />
      <Route path={RouteTemplates.CollaboratorViewPlanItem} component={DevPlanItem} />
      <Route path={RouteTemplates.CollaboratorDevPlan} component={DevPlanView} />
    </Switch>
  );
};

export default CollaboratorDevPlanRouter;
